import React, { useEffect } from 'react'
import {
  createStyles,
  makeStyles,
  TextField,
  Box,
  Button,
  InputLabel,
  CircularProgress,
} from '@material-ui/core'

import { toast } from 'react-toastify'
import { omit, throttle } from 'lodash'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { updateUserInfo } from 'src/store/slices/authSlice'
import FocusError from './FocusError'

import { oriSchema } from 'src/utils/schema'
import { useTranslateFormErrors } from 'src/hooks'

const useStyles = makeStyles((theme) =>
  createStyles({
    form: {
      backgroundColor: theme.palette.background.paper,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(2),
    },
    submitButton: {
      height: theme.spacing(5),
    },
    textField: {
      marginBottom: theme.spacing(1.5),
      marginTop: theme.spacing(1.5),
    },
    radioGroup: {
      marginTop: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.grey[50]}`,
    },
    arrowIcon: {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      '& path': {
        fill: theme.palette.grey[100],
      },
    },
    formControlLabel: {
      color: theme.palette.grey[200],
    },
    inputBorder: {
      border: '1px solid',
    },
    label: {
      minWidth: theme.spacing(10),
      display: 'flex',
      flexDirection: 'row-reverse',
      justifyContent: 'flex-end',
      '& .MuiInputLabel-outlined': {
        display: 'flex',
        flexDirection: 'row-reverse',
      },
      '& .MuiFormLabel-asterisk': {
        color: theme.palette.secondary.main,
      },
    },
  }),
)

const UserInfoForm = ({ userInfo, onConfirm, onCancel, fromOrder, field }) => {
  const initialValues = {
    phone: '',
    whatsAppId: userInfo?.whatsAppId || userInfo?.phone || '',
    weChatId: userInfo?.weChatId || '',
    id: userInfo.id,
  }
  const classes = useStyles()
  const dispatch = useDispatch()

  const { t } = useTranslation()
  const { updateUserInfoStatus } = useSelector((state) => state.auth)
  const schema = oriSchema(t).pick(['weChatId', 'whatsAppId', 'phone'])

  const handleConfirm = (values) => {
    onConfirm && onConfirm(values)
  }

  return (
    <Formik
      initialValues={initialValues}
      // from add order page user info form should validate phone and email field.
      validationSchema={
        field === 'weChatId' ? schema.omit(['phone', 'whatsAppId']) : schema.omit(['phone', 'weChatId'])
      }
      onSubmit={throttle(async (values, actions) => {
        if (updateUserInfoStatus === 'pending') return

        const pureValues = omit(values, [field === 'weChatId' ? 'whatsAppId' : 'weChatId', 'phone'])

        await dispatch(updateUserInfo(pureValues)).unwrap()

        toast.success(t('tips.change_info_successfully'))
        handleConfirm(pureValues)
      }, 1000)}
    >
      {(props) => {
        const {
          values,

          touched,
          errors,
          handleChange,
          handleSubmit,

          // dirty,
          setFieldTouched,
          isSubmitting,
        } = props

        useEffect(() => {
          if (fromOrder) {
            setFieldTouched('phone')
          }
        }, [])

        const isError = (field) => touched[field] && Boolean(errors[field])
        const errorText = (field) => touched[field] && errors[field]
        useTranslateFormErrors(errors, touched, setFieldTouched)

        return (
          <form className={classes.form} onSubmit={handleSubmit} noValidate>
            <InputLabel className={classes.label} required>
              {field === 'whatsAppId' ? t('profile.whatsapp_number') : t('profile.wechat_number')}
            </InputLabel>
            {field === 'whatsAppId' ? (
              <TextField
                className={classes.textField}
                id="whatsApp-id-field"
                name="whatsAppId"
                variant="outlined"
                fullWidth
                onChange={handleChange}
                value={values.whatsAppId}
                placeholder={`${t('validation.please_enter')}WhatsApp`}
                error={isError('whatsAppId')}
                helperText={errorText('whatsAppId')}
                type="text"
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            ) : (
              <TextField
                className={classes.textField}
                id={field + '-id-field'}
                name={field}
                fullWidth
                variant="outlined"
                required
                value={values.weChatId}
                onChange={handleChange}
                error={isError(field)}
                helperText={errorText(field)}
                type="text"
              />
            )}

            <Box mt={6} mb={2}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isSubmitting || !values[field]}
                color="secondary"
                className={classes.submitButton}
              >
                {updateUserInfoStatus === 'pending' ? (
                  <CircularProgress color="inherit" size={24} />
                ) : (
                  t('common.save_change')
                )}
              </Button>
            </Box>
            <Button variant="outlined" fullWidth color="default" onClick={handleConfirm}>
              {t('common.cancel')}
            </Button>
            <FocusError />
          </form>
        )
      }}
    </Formik>
  )
}

export default UserInfoForm
